import type {
    Job,
    OrderResponse,
    SessionResponse,
    StaticPage,
    UploadTicket,
} from './types';

// Empty by default: the SPA and the API share the CloudFront domain, so the
// routes below are requested relative to the current origin.
const BASE_URL = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/+$/, '');
const TOKEN_STORAGE_KEY = 'fdm.session.token';

/** Error carrying the machine readable code returned by the API. */
export class ApiError extends Error {
    readonly code: string;
    readonly status: number;

    constructor(message: string, code: string, status: number) {
        super(message);
        this.name = 'ApiError';
        this.code = code;
        this.status = status;
    }
}

interface Envelope<T> {
    ok: boolean;
    data?: T;
    error?: { code: string; message: string };
}

function readToken(): string | null {
    try {
        return window.localStorage.getItem(TOKEN_STORAGE_KEY);
    } catch {
        return null;
    }
}

function writeToken(token: string): void {
    try {
        window.localStorage.setItem(TOKEN_STORAGE_KEY, token);
    } catch {
        /* private mode: the token simply lives in memory for this page view */
    }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
    const token = readToken();
    const headers = new Headers(init.headers);
    headers.set('Accept', 'application/json');
    if (init.body) {
        headers.set('Content-Type', 'application/json');
    }
    if (token) {
        headers.set('Authorization', `Bearer ${token}`);
    }

    let response: Response;
    try {
        response = await fetch(`${BASE_URL}${path}`, { ...init, headers });
    } catch {
        throw new ApiError(
            'The service is unreachable, please check your connection and try again',
            'network_error',
            0,
        );
    }

    let envelope: Envelope<T> | null = null;
    try {
        envelope = (await response.json()) as Envelope<T>;
    } catch {
        envelope = null;
    }

    if (!response.ok || !envelope?.ok) {
        throw new ApiError(
            envelope?.error?.message ?? 'Something went wrong, please try again',
            envelope?.error?.code ?? 'unknown_error',
            response.status,
        );
    }
    return envelope.data as T;
}

export const api = {
    /** Creates (or refreshes) the anonymous session and returns the site config. */
    async session(): Promise<SessionResponse> {
        const data = await request<SessionResponse>('/v1/session', { method: 'POST' });
        writeToken(data.token);
        return data;
    },

    /** Registers an upload and returns the presigned S3 PUT ticket. */
    createUpload(fileName: string, size: number): Promise<UploadTicket> {
        return request<UploadTicket>('/v1/uploads', {
            method: 'POST',
            body: JSON.stringify({ fileName, size }),
        });
    },

    job(jobId: string): Promise<Job> {
        return request<Job>(`/v1/jobs/${encodeURIComponent(jobId)}`);
    },

    createOrder(jobId: string): Promise<OrderResponse> {
        return request<OrderResponse>('/v1/payments/paypal/orders', {
            method: 'POST',
            body: JSON.stringify({ jobId }),
        });
    },

    captureOrder(jobId: string, orderId: string): Promise<Job> {
        return request<Job>('/v1/payments/paypal/captures', {
            method: 'POST',
            body: JSON.stringify({ jobId, orderId }),
        });
    },

    download(jobId: string): Promise<{ downloadUrl: string; fileName: string }> {
        return request(`/v1/jobs/${encodeURIComponent(jobId)}/download`);
    },

    page(slug: string): Promise<StaticPage> {
        return request<StaticPage>(`/v1/pages/${encodeURIComponent(slug)}`);
    },
};

/**
 * Uploads the file straight to S3 with the presigned URL.
 *
 * XMLHttpRequest is used instead of fetch because it is the only way to get
 * real upload progress events, which the legacy socket.io transport provided.
 */
export function uploadToS3(
    ticket: UploadTicket,
    file: File,
    onProgress: (percent: number) => void,
): Promise<void> {
    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', ticket.uploadUrl, true);
        xhr.setRequestHeader('Content-Type', ticket.contentType);

        xhr.upload.onprogress = (event) => {
            if (event.lengthComputable) {
                onProgress(Math.round((event.loaded / event.total) * 100));
            }
        };
        xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
                onProgress(100);
                resolve();
                return;
            }
            reject(new ApiError('The upload was rejected, please try again', 'upload_failed', xhr.status));
        };
        xhr.onerror = () =>
            reject(new ApiError('The upload failed, please check your connection', 'network_error', 0));
        xhr.onabort = () => reject(new ApiError('The upload was cancelled', 'aborted', 0));

        xhr.send(file);
    });
}
